/**
 * Encroachment Imagery Comparison Component
 * Displays before/after satellite imagery for encroachment alerts with AI analysis
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaTimes, 
  FaSpinner, 
  FaExclamationTriangle, 
  FaCheckCircle,
  FaSatellite,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaBrain,
  FaLeaf,
  FaChartBar
} from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import encroachmentImageryService from '../../services/encroachmentImageryService';
import './EncroachmentImageryComparison.css';

const EncroachmentImageryComparison = ({ 
  alert, 
  onClose, 
  testMode = false,
  testComparisonData = null,
  testComparisonLoading = false,
  testComparisonError = null 
}) => {
  // State for imagery
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  
  // State for AI analysis - Segmentation (Primary Analysis)
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  /**
   * Check imagery availability on mount
   */
  useEffect(() => {
    if (testMode) {
      // In test mode, use the provided test comparison data
      if (testComparisonData) {
        setComparisonData(testComparisonData);
        performAIAnalysis(testComparisonData);
      }
      if (testComparisonError) {
        setError(testComparisonError);
      }
      setLoading(testComparisonLoading);
    } else {
      // Normal mode - check availability
      checkAvailability();
    }
  }, [alert, testMode, testComparisonData, testComparisonError, testComparisonLoading]);

  /**
   * Check if imagery is available for this alert
   */
  const checkAvailability = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const availabilityData = await encroachmentImageryService.checkImageryAvailability(
        alert.latitude,
        alert.longitude,
        alert.date
      );
      
      setAvailability(availabilityData);
      
      // If available, automatically fetch images
      if (availabilityData.available) {
        await fetchComparisonImages();
      }
      
    } catch (err) {
      console.error('Error checking availability:', err);
      setError(err.message || 'Failed to check imagery availability');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch comparison images
   */
  const fetchComparisonImages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const comparison = await encroachmentImageryService.getComparisonImages(
        alert.latitude,
        alert.longitude,
        alert.date
      );
      
      setComparisonData(comparison);
      
      // Trigger AI analysis after images are loaded
      performAIAnalysis(comparison);
      
    } catch (err) {
      console.error('Error fetching comparison images:', err);
      setError(err.message || 'Failed to fetch comparison images');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Perform AI Segmentation Analysis (Primary Analysis Method)
   */
  const performAIAnalysis = useCallback(async (data) => {
    try {
      setAiLoading(true);
      setAiError(null);
      
      let analysis;
      if (testMode) {
        // Use encroachment imagery service for test mode to get same prompt/analysis
        console.log('🧪 TEST: Starting AI analysis for encroachment test demo using encroachment prompt...');
        
        // Add encroachment_context if missing (for test mode compatibility)
        const enrichedData = {
          ...data,
          encroachment_context: data.encroachment_context || {
            alert_date: data.image2?.actual_date || new Date().toISOString().split('T')[0],
            confidence: 'high',
            alert_type: 'test_demo'
          }
        };
        
        analysis = await encroachmentImageryService.analyzeSegmentation(enrichedData);
      } else {
        // Use encroachment imagery service for normal mode
        analysis = await encroachmentImageryService.analyzeSegmentation(data);
      }
      
      setAiAnalysis(analysis);
      
    } catch (err) {
      console.error('AI Analysis failed:', err);
      setAiError(err.message || 'Failed to perform AI analysis');
    } finally {
      setAiLoading(false);
    }
  }, [testMode]);

  /**
   * Render unavailability message
   */
  const renderUnavailableMessage = () => (
    <div className="unavailable-message">
      <FaExclamationTriangle className="warning-icon" />
      <h3>After Image Not Yet Available</h3>
      <p>{availability?.message}</p>
      <div className="date-info">
        <div className="date-item">
          <strong>Alert Date:</strong> {encroachmentImageryService.formatDate(availability?.alert_date)}
        </div>
        <div className="date-item">
          <strong>Before Date:</strong> {encroachmentImageryService.formatDate(availability?.before_date)}
        </div>
        <div className="date-item">
          <strong>After Date (Expected):</strong> {encroachmentImageryService.formatDate(availability?.after_date)}
        </div>
      </div>
      <p className="help-text">
        The system searches for images within 7 days before and after the alert date. 
        Sentinel satellite imagery typically has a 3-5 day processing buffer. 
        Please check back after the expected date to view the comparison imagery.
      </p>
    </div>
  );

  /**
   * Render comparison images
   */
  const renderComparisonImages = () => {
    if (!comparisonData) return null;

    return (
      <div className="comparison-images-container">
        <div className="images-header">
          <h3>
            <FaSatellite /> 
            Satellite Imagery Comparison
            {testMode && (
              <span className="test-mode-badge">🧪 Test Demo</span>
            )}
          </h3>
          <div className="alert-context">
            {comparisonData.encroachment_context && (
              <div className="context-item">
                <FaCalendarAlt /> <strong>Alert Date:</strong> {encroachmentImageryService.formatDate(comparisonData.encroachment_context.alert_date)}
              </div>
            )}
            <div className="context-item">
              <FaMapMarkerAlt /> <strong>Location:</strong> {comparisonData.location.latitude.toFixed(4)}°, {comparisonData.location.longitude.toFixed(4)}°
            </div>
          </div>
        </div>

        <div className="image-comparison-grid">
          {/* Before Image */}
          <div className="image-panel">
            <div className="image-header">
              <h4>
                {testMode ? 'Before Image' : 'Before (closest within 7 days before alert)'}
              </h4>
              <p className="image-date">
                {encroachmentImageryService.formatDate(comparisonData.image1.actual_date)}
                {comparisonData.encroachment_context && comparisonData.encroachment_context.days_before_alert > 0 && (
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    ({comparisonData.encroachment_context.days_before_alert} days before)
                  </span>
                )}
              </p>
            </div>
            <div className="image-wrapper">
              {comparisonData.image1.preview_base64 ? (
                <img 
                  src={comparisonData.image1.preview_base64} 
                  alt={`Before - ${comparisonData.image1.actual_date}`}
                  className="satellite-image"
                />
              ) : (
                <div className="image-placeholder">
                  <FaSpinner className="spinner" />
                  <p>Loading image...</p>
                </div>
              )}
            </div>
            <div className="image-metadata">
              <div className="metadata-item">
                <strong>Satellite:</strong> {comparisonData.image1.satellite_id || 'Sentinel-2'}
              </div>
              <div className="metadata-item">
                <strong>Resolution:</strong> {comparisonData.image1.pixel_resolution || 10}m
              </div>
            </div>
          </div>

          {/* After Image */}
          <div className="image-panel">
            <div className="image-header">
              <h4>
                {testMode ? 'After Image' : 'After (closest within 7 days after alert)'}
              </h4>
              <p className="image-date">
                {encroachmentImageryService.formatDate(comparisonData.image2.actual_date)}
                {comparisonData.encroachment_context && comparisonData.encroachment_context.days_after_alert > 0 && (
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    ({comparisonData.encroachment_context.days_after_alert} days after)
                  </span>
                )}
              </p>
            </div>
            <div className="image-wrapper">
              {comparisonData.image2.preview_base64 ? (
                <img 
                  src={comparisonData.image2.preview_base64} 
                  alt={`After - ${comparisonData.image2.actual_date}`}
                  className="satellite-image"
                />
              ) : (
                <div className="image-placeholder">
                  <FaSpinner className="spinner" />
                  <p>Loading image...</p>
                </div>
              )}
            </div>
            <div className="image-metadata">
              <div className="metadata-item">
                <strong>Satellite:</strong> {comparisonData.image2.satellite_id || 'Sentinel-2'}
              </div>
              <div className="metadata-item">
                <strong>Resolution:</strong> {comparisonData.image2.pixel_resolution || 10}m
              </div>
            </div>
          </div>
        </div>

        <div className="comparison-stats">
          {!testMode && (
            <div className="stat-item">
              <strong>Search Window:</strong> ±7 days from alert date
            </div>
          )}
          <div className="stat-item">
            <strong>Days Between Images:</strong> {comparisonData.comparison?.days_between || 'N/A'} days
          </div>
          {comparisonData.encroachment_context && (
            <>
              <div className="stat-item">
                <strong>Before Image:</strong> {comparisonData.encroachment_context.days_before_alert} days before alert
              </div>
              <div className="stat-item">
                <strong>After Image:</strong> {comparisonData.encroachment_context.days_after_alert} days after alert
              </div>
            </>
          )}
          {testMode && (
            <div className="stat-item">
              <strong>Demo Status:</strong> Using pre-stored test images
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Render AI analysis tab (matching satellite comparison UI)
   */
  const renderAIAnalysis = () => {
    return (
      <div className="ai-analysis-section">
        <div className="ai-analysis-header">
          <FaBrain className="ai-icon" />
          <div>
            <h3>AI-Powered Encroachment Detection Analysis</h3>
            <p className="ai-subtitle">
              Gemini AI using segmentation to identify forest encroachment, vegetation loss, and environmental changes
            </p>
          </div>
        </div>

        {/* Loading State */}
        {aiLoading && (
          <div className="ai-loading">
            <FaSpinner className="spinner-icon" />
            <div className="loading-text">
              <h4>Analyzing Images with Gemini AI...</h4>
              <p>Identifying encroachment patterns, forest areas, cleared land, and environmental changes</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {aiError && !aiLoading && (
          <div className="ai-error">
            <FaExclamationTriangle className="error-icon" />
            <div className="error-text">
              <h4>Analysis Error</h4>
              <p>{aiError}</p>
              <p className="error-fallback">Displaying images without AI analysis. You can still manually compare the images in the Images tab.</p>
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {aiAnalysis && !aiLoading && !aiError && (
          <div className="ai-analysis-results">
            {/* Detailed Analysis Text */}
            <div className="analysis-content">
              <div className="analysis-header-info">
                <FaCheckCircle className="info-icon success-icon" />
                <div>
                  <h4>Detailed Analysis Report</h4>
                  {/* {aiAnalysis.metadata && aiAnalysis.metadata.used_fallback && (
                    <p className="model-info fallback-info">
                      ℹ️ Analysis generated using fallback model: <strong>{aiAnalysis.model_used}</strong>
                    </p>
                  )}
                  {aiAnalysis.model_used && !aiAnalysis.metadata?.used_fallback && (
                    <p className="model-info primary-info">
                      ✓ Model: <strong>{aiAnalysis.model_used}</strong>
                    </p>
                  )} */}
                  {aiAnalysis.encroachment_detected && (
                    <div className="detection-badge detected">
                      <FaExclamationTriangle /> Encroachment Detected
                    </div>
                  )}
                </div>
              </div>
              <div className="analysis-text markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1 className="markdown-h1" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="markdown-h2" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 className="markdown-h3" {...props} />
                    ),
                    h4: ({ node, ...props }) => (
                      <h4 className="markdown-h4" {...props} />
                    ),
                    h5: ({ node, ...props }) => (
                      <h5 className="markdown-h5" {...props} />
                    ),
                    h6: ({ node, ...props }) => (
                      <h6 className="markdown-h6" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="markdown-p" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="markdown-ul" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="markdown-ol" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="markdown-li" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="markdown-strong" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="markdown-em" {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote className="markdown-blockquote" {...props} />
                    ),
                    code: ({ node, inline, ...props }) => (
                      <code className={inline ? "markdown-code-inline" : "markdown-code-block"} {...props} />
                    ),
                    hr: ({ node, ...props }) => (
                      <hr className="markdown-hr" {...props} />
                    ),
                  }}
                >
                  {aiAnalysis.analysis_text}
                </ReactMarkdown>
              </div>
              
              {/* Analysis Metadata */}
              <div className="analysis-metadata">
                <div className="metadata-item">
                  <FaBrain className="metadata-icon" />
                  <span className="metadata-label">Approach:</span>
                  <span className="metadata-value">Segmentation-based</span>
                </div>
                <div className="metadata-item">
                  <FaBrain className="metadata-icon" />
                  <span className="metadata-label">Model:</span>
                  <span className="metadata-value">{aiAnalysis.model_used}</span>
                </div>
                <div className="metadata-item">
                  <FaCheckCircle className="metadata-icon" />
                  <span className="metadata-label">Analysis Date:</span>
                  <span className="metadata-value">{new Date().toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Default message if no analysis yet */}
        {!aiAnalysis && !aiLoading && !aiError && (
          <div className="ai-placeholder">
            <FaBrain className="placeholder-icon" />
            <div className="placeholder-content">
              <h4>AI Analysis Available</h4>
              <p>
                AI-powered analysis will appear here to identify forest encroachment patterns, vegetation loss,
                cleared land, and environmental transformations between the before and after images.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="encroachment-imagery-modal">
      <div className="modal-overlay" onClick={onClose} />
      
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <div className="header-left">
            <h2>
              <FaLeaf /> 
              {testMode ? 'Test Demo - Encroachment Detection' : 'Encroachment Imagery Analysis'}
            </h2>
            <p className="header-subtitle">
              {testMode ? (
                'Pre-stored test images with AI analysis'
              ) : (
                `Alert: ${alert.confidence.toUpperCase()} confidence | ${encroachmentImageryService.formatDate(alert.date)}`
              )}
            </p>
          </div>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="loader"></div>
              <p>Loading imagery...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="error-state">
              <FaExclamationTriangle className="error-icon-large" />
              <h3>Error Loading Imagery</h3>
              <p>{error}</p>
              <button className="retry-button" onClick={checkAvailability}>
                Try Again
              </button>
            </div>
          )}

          {/* Unavailable State */}
          {!loading && !error && availability && !availability.available && renderUnavailableMessage()}

          {/* Images and AI Analysis - Single Scrollable View */}
          {!loading && !error && comparisonData && (
            <div className="content-container">
              {renderComparisonImages()}
              {renderAIAnalysis()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EncroachmentImageryComparison;

